'use client';
import { Dispatch, FunctionComponent, memo, SetStateAction, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import AppliedFilterInfo from './AppliedFilterInfo';
import { useNavigate } from 'react-router-dom';
import { DetectedDefinition } from '../../../schemas/DetectedDefinition';
import { DynamicRequestBody } from '../../../schemas/DynamicRequestBody';
import { FilterParameterAndParameterValues } from '../ProductHome';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import PageContainer from '../tableComponents/PageContainer';
import { getDefinitionDisplayName } from '../../../utils/DefinitionDisplay';
import { ReturnParameterValuesLite } from '../../../schemas/ReturnParameterValuesLite';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';

interface SubLevelInfoProps {
    multipleSelectKeys: Map<string, FilterParameterAndParameterValues>;
    productField: DetectedDefinition;
    singleSelectKey: Map<string, FilterParameterAndParameterValues>;
    setGlobalFilterState: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    definitionsMap: Map<string, Map<string, DetectedDefinition>>;
    language: string;
    onlyHandleFilterChange: (parameter?: string) => void;
    setGlobalFilterStateAfterApply: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    productChildField: DetectedDefinition | undefined;
    queryRawResults: ReturnParameterValuesLite | undefined;
    isFilterControllerOpen: boolean;
    setProductChildField: Dispatch<SetStateAction<DetectedDefinition | undefined>>;
    contentTree: ProductsContent;
    setAppliedKeys: Dispatch<SetStateAction<Set<string>>>;
}

interface NavItemProps {
    def: DetectedDefinition;
    language: string;
    handleNavigate: (def: DetectedDefinition) => void;
}

const NavItem: FunctionComponent<NavItemProps> = memo(({ def, language, handleNavigate }) => {
    return (
        <Box key={def.displayName} sx={{ display: 'flex', alignItems: 'center' }}>
            {/* 小圓點 */}
            <Box
                component="span"
                sx={{
                    width: '0.5rem',
                    height: '0.5rem',
                    backgroundColor: '#B75332',
                    borderRadius: '50%',
                    display: 'inline-block',
                    mr: '0.5rem',
                    flex: '0 0 auto',
                }}
            />

            {/* 這個容器是「單一個」flex item，裡面再放 name 與 count */}
            <Box
                sx={{
                    display: 'inline', // 不是 flex item 分段：讓 name+count 成為同一塊
                    minWidth: 0, // 允許內容內部換行
                    lineHeight: 1.2,
                }}
            >
                {/* name：可點、橘色、允許換行 */}
                <Typography
                    component="span"
                    variant="h6"
                    sx={{
                        color: '#B75332',
                        cursor: 'pointer',
                        '&:hover': { color: '#E5673E' },
                        fontWeight: 400,
                        fontFamily: 'Poppins',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                    }}
                    onClick={() => handleNavigate(def)}
                    data-testid={`${def.displayName}-banner`}
                >
                    {getDefinitionDisplayName(def, language)}
                </Typography>

                {/* count：黑色、不可點。前面用普通空白即可，會跟在最後一行 */}
                <Typography
                    component="span"
                    variant="h6"
                    sx={{
                        color: 'black',
                        ml: '5px',
                        fontWeight: 400,
                        fontFamily: 'Poppins',
                        cursor: 'default',
                    }}
                >
                    ({def?.partCount?.toLocaleString()})
                </Typography>
            </Box>
        </Box>
    );
});

NavItem.displayName = 'NavItem';

const SubLevelInfo: FunctionComponent<SubLevelInfoProps> = ({
    singleSelectKey,
    multipleSelectKeys,
    productField,
    setGlobalFilterState,
    definitionsMap,
    language,
    onlyHandleFilterChange,
    setGlobalFilterStateAfterApply,
    productChildField,
    queryRawResults,
    isFilterControllerOpen,
    setProductChildField,
    contentTree,
    setAppliedKeys,
}) => {
    const navigate = useNavigate();

    const handleNavigate = useCallback(
        (def: DetectedDefinition) => {
            setGlobalFilterStateAfterApply((prev) => {
                const treeId =
                    definitionsMap.get(productField.displayName)?.get(productField.displayName)
                        ?.id ?? 0;

                return { ...prev, '1': treeId, '79': [def.id] };
            });

            if (productField.id !== def.id) {
                setProductChildField(def);
            }

            setAppliedKeys(new Set(['79']));

            const newRoute =
                productField.id !== def.id
                    ? `/products/${productField.displayName}?79=${def.id}`
                    : `/products/${productField.displayName}`;

            navigate(newRoute);
        },
        [navigate, productField]
    );

    const { isMaxScreen, isScreenXxl, isScreenMd, isScreenSm, isScreenXs, responsiveWidth } =
        useResponsive();

    // 當不是 isMaxScreen 或 isScreenXxl 時，包上 PageContainer
    const shouldWrapWithPageContainer = !(isMaxScreen || isScreenXxl);

    const useMobileProductDisplay = isScreenMd || isScreenSm || isScreenXs;

    const getInnerContentPadding = () => {
        if (shouldWrapWithPageContainer) {
            return 0;
        }
        return queryRawResults ? '1rem 2rem 1rem 2rem' : '1rem 2rem';
    };

    // 內部共用內容
    const innerContent = (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                padding: getInnerContentPadding(),
                width: '100%',
                boxSizing: 'border-box',
                flexDirection: 'column',
                overflowY: 'hidden',
            }}
            data-testid={'innerContent'}
        >
            {!queryRawResults && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        borderBottom: '1px solid #B75332',
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    {(!useMobileProductDisplay || !isFilterControllerOpen) && (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                padding: shouldWrapWithPageContainer ? '0' : '0 20px',
                                fontWeight: 400,
                                fontFamily: 'Poppins',
                            }}
                            data-testid={'nav-bar'}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: '10px',
                                }}
                            >
                                {Array.from(
                                    definitionsMap.get(productField.displayName) ?? new Map()
                                )
                                    .filter(([defName]) => defName !== productField.displayName)
                                    .map(([_, def]) => (
                                        <NavItem
                                            key={def.id}
                                            def={def}
                                            language={language}
                                            handleNavigate={handleNavigate}
                                        />
                                    ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
            {(!useMobileProductDisplay || isFilterControllerOpen) && (
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        '&::after': queryRawResults
                            ? {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  left: '1.2rem',
                                  right: '1rem',
                                  height: '1px',
                                  backgroundColor: '#E5673E',
                              }
                            : undefined,
                    }}
                >
                    <AppliedFilterInfo
                        productField={productField}
                        setGlobalFilterState={setGlobalFilterState}
                        singleSelectKey={singleSelectKey}
                        multipleSelectKeys={multipleSelectKeys}
                        isDataSheet={false}
                        onlyHandleFilterChange={onlyHandleFilterChange}
                        setGlobalFilterStateAfterApply={setGlobalFilterStateAfterApply}
                        productChildField={productChildField}
                        contentTree={contentTree}
                    />
                </Box>
            )}
        </Box>
    );

    return (
        <Box sx={{ width: '100%', backgroundColor: 'white' }} data-testid="sub-level">
            {shouldWrapWithPageContainer ? (
                <PageContainer
                    innerSx={{ width: responsiveWidth, paddingLeft: 0, paddingRight: 0 }}
                    containerName={'sub-level-responsive'}
                >
                    {innerContent}
                </PageContainer>
            ) : (
                innerContent
            )}
        </Box>
    );
};

export default SubLevelInfo;
